import { useCallback, useEffect, useState } from "react";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { fetchSatCycleMonitor } from "@/lib/platformApi";

const DEFAULT_RFC = "BVL1908211D6";

/** Mensaje amigable en preview sin tocar lógica SAT del backend. */
export function formatSatCycleMonitorDisplayError(message) {
  const msg = String(message || "").trim();
  if (!msg) return msg;
  if (/cliente no encontrado/i.test(msg)) {
    return "Cliente piloto no encontrado en este entorno de prueba. Verifique el RFC o cargue el cliente en la base de datos del preview.";
  }
  return msg;
}

/**
 * Monitor de primer ciclo SAT — solo lectura (Fase 4.96B).
 * @param {{ embedded?: boolean }} props
 */
export default function SatCycleMonitorPanel({ embedded = false }) {
  const [rfc, setRfc] = useState(DEFAULT_RFC);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const rfcU = (rfc || "").trim().toUpperCase();
    if (rfcU.length < 10) {
      toast.error("RFC inválido");
      return;
    }
    setLoading(true);
    try {
      const out = await fetchSatCycleMonitor(rfcU);
      setData(out);
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo cargar cycle-monitor"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rfc]);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- carga inicial BVL

  const cycleState = data?.cycle_state || "—";
  const delta = data?.readiness_summary?.controlled_delta_range || {};

  return (
    <div
      className={
        embedded
          ? "space-y-3"
          : "rounded-md border border-sky-200 bg-sky-50/50 p-4 space-y-3"
      }
      data-testid="sat-cycle-monitor-panel"
    >
      {!embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-[#0F2D5C] flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Monitor primer ciclo SAT
            </h2>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl">
              Solo lectura: robot, cola, locks, cooling-off, bitácora y checkpoint scheduler.
              No ejecuta descargas ni modifica el robot.
            </p>
          </div>
          <span
            className="text-xs font-semibold px-2 py-1 rounded-full bg-white border border-sky-200 text-sky-900"
            data-testid="sat-cycle-state-badge"
          >
            {cycleState}
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-600 m-0 max-w-2xl">
            RFC piloto por defecto: <span className="font-mono">{DEFAULT_RFC}</span>. Solo lectura — no ejecuta
            descargas.
          </p>
          <span
            className="text-xs font-semibold px-2 py-1 rounded-full bg-white border border-sky-200 text-sky-900"
            data-testid="sat-cycle-state-badge"
          >
            {cycleState}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="cycle-monitor-rfc" className="text-xs">
            RFC piloto
          </Label>
          <Input
            id="cycle-monitor-rfc"
            value={rfc}
            onChange={(e) => setRfc(e.target.value.toUpperCase())}
            className="h-8 w-40 text-sm font-mono"
            data-testid="sat-cycle-monitor-rfc"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="sat-cycle-monitor-refresh"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-1">Actualizar</span>
        </Button>
      </div>

      {data?.ok ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs text-slate-700">
          <div>
            <dt className="font-medium text-slate-500">Robot</dt>
            <dd>{data.robot?.status_label || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Readiness</dt>
            <dd>{data.readiness_summary?.status || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Cola</dt>
            <dd>
              {data.queue?.verdict} · Q:{data.queue?.jobs_queued} R:{data.queue?.jobs_running}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Locks huérfanos</dt>
            <dd>{data.locks?.orphan_lock_detected ? "Sí" : "No"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Cooling-off</dt>
            <dd>{data.cooling?.has_cooling_off ? "Activo" : "No"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Delta esperado</dt>
            <dd className="font-mono">
              {delta.from || "—"} → {delta.to || "—"}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Último SUCCESS</dt>
            <dd className="font-mono text-[11px]">
              {data.sync_logs?.last_success_at
                ? String(data.sync_logs.last_success_at).slice(0, 19)
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Scheduler checkpoint</dt>
            <dd className="font-mono text-[11px]">
              {data.scheduler?.last_completed_at
                ? `${String(data.scheduler.last_rfc || "")} @ ${String(data.scheduler.last_completed_at).slice(0, 19)}`
                : "Sin checkpoint"}
            </dd>
          </div>
          {data.robot?.paused_reason ? (
            <div className="sm:col-span-2">
              <dt className="font-medium text-slate-500">
                Motivo al apagar robot
                {data.robot?.paused_reason_historical ? (
                  <span className="ml-1 font-normal text-amber-700">(histórico)</span>
                ) : null}
              </dt>
              <dd className="text-[11px] text-slate-700">{data.robot.paused_reason}</dd>
              {data.robot?.paused_reason_note ? (
                <dd className="text-[11px] text-amber-800 mt-0.5">{data.robot.paused_reason_note}</dd>
              ) : null}
            </div>
          ) : null}
        </dl>
      ) : data?.error ? (
        <p className="text-xs text-red-700" data-testid="sat-cycle-monitor-error">
          {formatSatCycleMonitorDisplayError(data.error)}
        </p>
      ) : null}
    </div>
  );
}
